import { createContext, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { empresasService } from "app/services";
import { toast } from "react-hot-toast";
import { ErrorMessage, Spinner } from "app/components";

const CelularesContext = createContext();

export function CelularesProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const celularesQuery = useQuery({
    queryKey: ["empresa:get:celulares", params.empresa_id],
    queryFn: () =>
      empresasService.getCelulares({ empresaId: params.empresa_id }),
  });

  const celularAddMutation = useMutation({
    queryKey: ["empresa:add:celular", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addCelular(data), {
        loading: "Agregando el número de celular...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await celularesQuery.refetch();
    },
  });

  const celularUpdateMutation = useMutation({
    queryKey: ["empresa:update:celular"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateCelular(data), {
        loading: "Actualizando el número de celular...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await celularesQuery.refetch();
    },
  });

  const celularDeleteMutation = useMutation({
    queryKey: ["empresa:delete:celular"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteCelular(data), {
        loading: "Eliminando el número de celular...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await celularesQuery.refetch();
    },
  });

  const celularVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:celular"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyCelular(data), {
        loading: "Verificando el número de celular...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await celularesQuery.refetch();
    },
  });

  async function addCelular({ celular }) {
    return await celularAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      celular,
    });
  }

  async function updateCelular({ celularId, celular }) {
    const { celular_verificado, ...rest } = celular;

    return await celularUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      celularId,
      celular: rest,
    });
  }

  async function deleteCelular({ celularId }) {
    const confirmed = window.confirm("¿Desea eliminar este número de celular?");

    if (confirmed) {
      await celularDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        celularId: celularId,
      });

      return true;
    }

    return false;
  }

  async function verifyCelular({ celularId }) {
    const confirmed = window.confirm(
      "¿Desea verificar este número de celular?"
    );

    if (confirmed) {
      return await celularVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        celularId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!celularesQuery.isLoading) {
    return <Spinner label="Cargando los números de celular..." />;
  } else if (!!celularesQuery.isError) {
    return <ErrorMessage message={celularesQuery.error.response.statusText} />;
  }

  return (
    <CelularesContext.Provider
      value={{
        celulares: celularesQuery.data,
        isLoading: celularesQuery.isLoading,
        isFetching: celularesQuery.isFetching,
        isRefetching: celularesQuery.isRefetching,
        addCelular,
        isAdding: celularAddMutation.isLoading,
        updateCelular,
        isUpdating: celularUpdateMutation.isLoading,
        deleteCelular,
        isDeleting: celularDeleteMutation.isLoading,
        verifyCelular,
        isVerifing: celularVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </CelularesContext.Provider>
  );
}

export function useCelularesContext() {
  const celularesContext = useContext(CelularesContext);

  if (!celularesContext) {
    throw new Error(
      "useCelularesContext should be used inside the CelularesProvider."
    );
  }

  return celularesContext;
}
